import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, Building2, Users } from 'lucide-react';

interface Workspace {
  id: string;
  name: string;
  organization?: {
    name: string;
  };
  plan?: {
    name: string;
  };
  member_count?: number;
}

interface WorkspacePickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaces: Workspace[];
  selectedWorkspaces: Set<string>;
  onSelectionChange: (workspaceIds: Set<string>) => void;
  onConfirm: () => void;
  title?: string;
  description?: string;
  isLoading?: boolean;
}

export function WorkspacePickerModal({
  open,
  onOpenChange,
  workspaces,
  selectedWorkspaces,
  onSelectionChange,
  onConfirm,
  title = "Select Workspaces",
  description = "Choose which workspaces to apply changes to.",
  isLoading = false
}: WorkspacePickerModalProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredWorkspaces = useMemo(() => {
    if (!searchQuery.trim()) return workspaces;
    
    const query = searchQuery.toLowerCase();
    return workspaces.filter(workspace =>
      workspace.name.toLowerCase().includes(query) ||
      workspace.organization?.name.toLowerCase().includes(query) ||
      workspace.plan?.name.toLowerCase().includes(query)
    );
  }, [workspaces, searchQuery]);

  const handleWorkspaceToggle = (workspaceId: string, checked: boolean) => {
    const newSelection = new Set(selectedWorkspaces);
    if (checked) {
      newSelection.add(workspaceId);
    } else {
      newSelection.delete(workspaceId);
    }
    onSelectionChange(newSelection);
  };

  const handleSelectAll = () => {
    if (selectedWorkspaces.size === filteredWorkspaces.length) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(filteredWorkspaces.map(w => w.id)));
    }
  };

  const isAllSelected = selectedWorkspaces.size === filteredWorkspaces.length && filteredWorkspaces.length > 0;
  const isPartiallySelected = selectedWorkspaces.size > 0 && selectedWorkspaces.size < filteredWorkspaces.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {title}
          </DialogTitle>
          <DialogDescription>
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search workspaces..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Select All */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
                <Checkbox
                  id="select-all"
                  checked={isAllSelected}
                  ref={(el) => {
                    if (el) {
                      (el as any).indeterminate = isPartiallySelected;
                    }
                  }}
                  onCheckedChange={handleSelectAll}
                />
              <label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
                Select All ({filteredWorkspaces.length} workspaces)
              </label>
            </div>
            <Badge variant="outline">
              {selectedWorkspaces.size} selected
            </Badge>
          </div>

          {/* Workspace List */}
          <ScrollArea className="max-h-96">
            <div className="space-y-2">
              {filteredWorkspaces.map((workspace) => (
                <Card key={workspace.id} className="hover:bg-muted/50 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id={`workspace-${workspace.id}`}
                        checked={selectedWorkspaces.has(workspace.id)}
                        onCheckedChange={(checked) => 
                          handleWorkspaceToggle(workspace.id, checked as boolean)
                        }
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium truncate">{workspace.name}</h4>
                          {workspace.plan && (
                            <Badge variant="secondary" className="text-xs">
                              {workspace.plan.name}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          {workspace.organization && (
                            <span className="truncate">
                              {workspace.organization.name}
                            </span>
                          )}
                          {workspace.member_count && (
                            <div className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              <span>{workspace.member_count} members</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {filteredWorkspaces.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Building2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No workspaces found</p>
                  {searchQuery && (
                    <p className="text-sm">Try adjusting your search query</p>
                  )}
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={onConfirm}
            disabled={selectedWorkspaces.size === 0 || isLoading}
          >
            {isLoading ? 'Processing...' : `Apply to ${selectedWorkspaces.size} workspaces`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}